import { formatCrossborderAmount, formatFxRate, formatPercent } from "../constants";

const SummaryRow = ({ label, value, bold = false, borderTop = false }) => (
  <div
    className={`flex justify-between ${borderTop ? "border-t border-gray-100 pt-3" : ""}`}
  >
    <span className={bold ? "font-semibold text-brand-dark" : "text-gray-500"}>{label}</span>
    <span className="text-brand-dark">{value}</span>
  </div>
);

export const CrossborderSummary = ({
  title = "Resumo",
  subtitle,
  rows = [],
  footerRows = [],
  actionLabel,
  onAction,
  children,
}) => (
  <section className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 lg:h-full">
    <h3 className="text-sm font-bold text-brand-dark">{title}</h3>
    {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}

    <div className="mt-3 space-y-2 text-sm">
      {rows.map((row) => (
        <SummaryRow key={row.key ?? row.label} {...row} />
      ))}
      {children}
    </div>

    {footerRows.length > 0 && (
      <div className="mt-2 space-y-2 text-sm">
        {footerRows.map((row) => (
          <SummaryRow key={row.key ?? row.label} {...row} />
        ))}
      </div>
    )}

    {actionLabel && onAction && (
      <button type="button" className="btn-primary mt-5 w-full" onClick={onAction}>
        {actionLabel}
      </button>
    )}
  </section>
);

export const buildPayinSummaryRows = (quote, { originRail, destinationRail }) => {
  const { originCurrency, destinationCurrency } = quote;

  const rows = [
    {
      key: "origin-country",
      label: "País de origem",
      value: originCurrency ? `${originCurrency.code}` : "—",
    },
    {
      key: "origin-rail",
      label: "Forma na origem",
      value: originRail?.label ?? "—",
    },
    {
      key: "destination-rail",
      label: "Recebimento na Valut",
      value: destinationRail?.label ?? "—",
    },
    {
      key: "amount",
      label: "Valor enviado",
      value:
        quote.hasAmount && originCurrency
          ? formatCrossborderAmount(quote.amount, originCurrency.id)
          : "—",
    },
    {
      key: "rate",
      label: "Câmbio",
      value:
        quote.hasAmount && originCurrency && destinationCurrency
          ? `1 ${originCurrency.code} = ${formatFxRate(quote.rate)} ${destinationCurrency.code}`
          : "—",
    },
    {
      key: "origin-fee",
      label:
        quote.originFeePercent != null
          ? `${quote.originFeeLabel} (${formatPercent(quote.originFeePercent)})`
          : quote.originFeeLabel,
      value:
        quote.hasAmount && originCurrency
          ? formatCrossborderAmount(quote.originFee, originCurrency.id)
          : "—",
    },
  ];

  if (quote.iofApplies) {
    rows.push({
      key: "iof",
      label: `IOF (${formatPercent(quote.iofPercent)})`,
      value:
        quote.hasAmount && originCurrency
          ? formatCrossborderAmount(quote.iof, originCurrency.id === "brl" ? "brl" : destinationCurrency?.id ?? "brl")
          : "—",
    });
  }

  rows.push({
    key: "conversion-fee",
    label: `Taxa crossborder (${formatPercent(quote.conversionFeePercent)})`,
    value:
      quote.hasAmount && destinationCurrency
        ? formatCrossborderAmount(quote.conversionFee, destinationCurrency.id)
        : "—",
  });

  if (quote.destinationFee > 0) {
    rows.push({
      key: "destination-fee",
      label:
        quote.destinationFeePercent != null
          ? `${quote.destinationFeeLabel} (${formatPercent(quote.destinationFeePercent)})`
          : quote.destinationFeeLabel,
      value:
        quote.hasAmount && destinationCurrency
          ? formatCrossborderAmount(quote.destinationFee, destinationCurrency.id)
          : "—",
    });
  }

  const footerRows = [
    {
      key: "net-receive",
      label: "Valor a creditar",
      value:
        quote.hasAmount && destinationCurrency
          ? formatCrossborderAmount(quote.netReceive, destinationCurrency.id)
          : "—",
      bold: true,
      borderTop: true,
    },
  ];

  return { rows, footerRows };
};

export const buildModalPayinSummaryRows = (quote) => {
  const { originCurrency, destinationCurrency } = quote;

  const rows = [
    {
      key: "route",
      label: "Rota",
      value:
        quote.originRail && quote.destinationRail && originCurrency && destinationCurrency
          ? `${quote.originRail.label} ${originCurrency.code} → ${quote.destinationRail.label} ${destinationCurrency.code}`
          : "—",
    },
    {
      key: "amount",
      label: "Valor na origem",
      value:
        quote.hasAmount && originCurrency
          ? formatCrossborderAmount(quote.amount, originCurrency.id)
          : "—",
    },
    {
      key: "rate",
      label: "Câmbio",
      value:
        quote.hasAmount && originCurrency && destinationCurrency
          ? `1 ${originCurrency.code} = ${formatFxRate(quote.rate)} ${destinationCurrency.code}`
          : "—",
    },
  ];

  if (quote.originFee > 0) {
    rows.push({
      key: "origin-fee",
      label:
        quote.originFeePercent != null
          ? `${quote.originFeeLabel} (${formatPercent(quote.originFeePercent)})`
          : quote.originFeeLabel,
      value:
        quote.hasAmount && originCurrency
          ? formatCrossborderAmount(quote.originFee, originCurrency.id)
          : "—",
    });
  }

  if (quote.iofApplies && quote.iof > 0) {
    rows.push({
      key: "iof",
      label: `IOF (${formatPercent(quote.iofPercent)})`,
      value:
        quote.hasAmount
          ? formatCrossborderAmount(quote.iof, originCurrency?.id === "brl" ? "brl" : destinationCurrency?.id ?? "brl")
          : "—",
    });
  }

  rows.push({
    key: "conversion-fee",
    label: `Taxa crossborder (${formatPercent(quote.conversionFeePercent)})`,
    value:
      quote.hasAmount && destinationCurrency
        ? formatCrossborderAmount(quote.conversionFee, destinationCurrency.id)
        : "—",
  });

  if (quote.destinationFee > 0) {
    rows.push({
      key: "destination-fee",
      label:
        quote.destinationFeePercent != null
          ? `${quote.destinationFeeLabel} (${formatPercent(quote.destinationFeePercent)})`
          : quote.destinationFeeLabel,
      value:
        quote.hasAmount && destinationCurrency
          ? formatCrossborderAmount(quote.destinationFee, destinationCurrency.id)
          : "—",
    });
  }

  if (quote.partnerFee > 0) {
    rows.push({
      key: "partner-fee",
      label: "Taxa do parceiro",
      value:
        quote.hasAmount && destinationCurrency
          ? formatCrossborderAmount(quote.partnerFee, destinationCurrency.id)
          : "—",
    });
  }

  const footerRows = [
    {
      key: "net-receive",
      label: "Cliente recebe",
      value:
        quote.hasAmount && destinationCurrency
          ? formatCrossborderAmount(quote.netReceive, destinationCurrency.id)
          : "—",
      bold: true,
      borderTop: true,
    },
  ];

  return { rows, footerRows };
};

export const buildModalPayoutSummaryRows = (quote) => {
  const { originCurrency, destinationCurrency } = quote;

  const rows = [
    {
      key: "route",
      label: "Rota",
      value:
        quote.sendRail && quote.beneficiaryRail && originCurrency && destinationCurrency
          ? `${quote.sendRail.label} ${originCurrency.code} → ${quote.beneficiaryRail.label} ${destinationCurrency.code}`
          : "—",
    },
    {
      key: "amount",
      label: "Valor a enviar",
      value:
        quote.hasAmount && originCurrency
          ? formatCrossborderAmount(quote.amount, originCurrency.id)
          : "—",
    },
    {
      key: "rate",
      label: "Câmbio",
      value:
        quote.hasAmount && originCurrency && destinationCurrency
          ? `1 ${originCurrency.code} = ${formatFxRate(quote.rate)} ${destinationCurrency.code}`
          : "—",
    },
    {
      key: "send-fee",
      label:
        quote.sendFeePercent != null
          ? `${quote.sendFeeLabel} (${formatPercent(quote.sendFeePercent)})`
          : quote.sendFeeLabel,
      value:
        quote.hasAmount && originCurrency
          ? formatCrossborderAmount(quote.sendFee, originCurrency.id)
          : "—",
    },
  ];

  if (quote.iofApplies && quote.iof > 0) {
    rows.push({
      key: "iof",
      label: `IOF (${formatPercent(quote.iofPercent)})`,
      value:
        quote.hasAmount
          ? formatCrossborderAmount(quote.iof, originCurrency?.id === "brl" ? "brl" : destinationCurrency?.id ?? "brl")
          : "—",
    });
  }

  rows.push({
    key: "conversion-fee",
    label: `Taxa crossborder (${formatPercent(quote.conversionFeePercent)})`,
    value:
      quote.hasAmount && destinationCurrency
        ? formatCrossborderAmount(quote.conversionFee, destinationCurrency.id)
        : "—",
  });

  if (quote.beneficiaryFee > 0) {
    rows.push({
      key: "beneficiary-fee",
      label:
        quote.beneficiaryFeePercent != null
          ? `${quote.beneficiaryFeeLabel} (${formatPercent(quote.beneficiaryFeePercent)})`
          : quote.beneficiaryFeeLabel,
      value:
        quote.hasAmount && destinationCurrency
          ? formatCrossborderAmount(quote.beneficiaryFee, destinationCurrency.id)
          : "—",
    });
  }

  if (quote.partnerFee > 0) {
    rows.push({
      key: "partner-fee",
      label: "Taxa do parceiro",
      value:
        quote.hasAmount && destinationCurrency
          ? formatCrossborderAmount(quote.partnerFee, destinationCurrency.id)
          : "—",
    });
  }

  const footerRows = [
    {
      key: "net-receive",
      label: "Beneficiário recebe",
      value:
        quote.hasAmount && destinationCurrency
          ? formatCrossborderAmount(quote.netReceive, destinationCurrency.id)
          : "—",
      bold: true,
    },
    {
      key: "total-debit",
      label: "Total a debitar",
      value:
        quote.hasAmount && originCurrency
          ? formatCrossborderAmount(quote.totalDebit, originCurrency.id)
          : "—",
      bold: true,
      borderTop: true,
    },
  ];

  return { rows, footerRows };
};

export const buildPayoutSummaryRows = (quote, { sendRail, beneficiaryRail }) => {
  const { originCurrency, destinationCurrency } = quote;

  const rows = [
    {
      key: "origin-currency",
      label: "Conta Valut",
      value: originCurrency?.code ?? "—",
    },
    {
      key: "send-rail",
      label: "Forma de envio",
      value: sendRail?.label ?? "—",
    },
    {
      key: "beneficiary-rail",
      label: "Recebimento no destino",
      value: beneficiaryRail?.label ?? "—",
    },
    {
      key: "amount",
      label: "Valor a enviar",
      value:
        quote.hasAmount && originCurrency
          ? formatCrossborderAmount(quote.amount, originCurrency.id)
          : "—",
    },
    {
      key: "rate",
      label: "Câmbio",
      value:
        quote.hasAmount && originCurrency && destinationCurrency
          ? `1 ${originCurrency.code} = ${formatFxRate(quote.rate)} ${destinationCurrency.code}`
          : "—",
    },
    {
      key: "send-fee",
      label:
        quote.sendFeePercent != null
          ? `${quote.sendFeeLabel} (${formatPercent(quote.sendFeePercent)})`
          : quote.sendFeeLabel,
      value:
        quote.hasAmount && originCurrency
          ? formatCrossborderAmount(quote.sendFee, originCurrency.id)
          : "—",
    },
  ];

  if (quote.iofApplies) {
    rows.push({
      key: "iof",
      label: `IOF (${formatPercent(quote.iofPercent)})`,
      value:
        quote.hasAmount
          ? formatCrossborderAmount(quote.iof, originCurrency?.id === "brl" ? "brl" : destinationCurrency?.id ?? "brl")
          : "—",
    });
  }

  rows.push({
    key: "conversion-fee",
    label: `Taxa crossborder (${formatPercent(quote.conversionFeePercent)})`,
    value:
      quote.hasAmount && destinationCurrency
        ? formatCrossborderAmount(quote.conversionFee, destinationCurrency.id)
        : "—",
  });

  if (quote.beneficiaryFee > 0) {
    rows.push({
      key: "beneficiary-fee",
      label:
        quote.beneficiaryFeePercent != null
          ? `${quote.beneficiaryFeeLabel} (${formatPercent(quote.beneficiaryFeePercent)})`
          : quote.beneficiaryFeeLabel,
      value:
        quote.hasAmount && destinationCurrency
          ? formatCrossborderAmount(quote.beneficiaryFee, destinationCurrency.id)
          : "—",
    });
  }

  const footerRows = [
    {
      key: "net-receive",
      label: "Beneficiário recebe",
      value:
        quote.hasAmount && destinationCurrency
          ? formatCrossborderAmount(quote.netReceive, destinationCurrency.id)
          : "—",
      bold: true,
    },
    {
      key: "total-debit",
      label: "Total a debitar",
      value:
        quote.hasAmount && originCurrency
          ? formatCrossborderAmount(quote.totalDebit, originCurrency.id)
          : "—",
      bold: true,
      borderTop: true,
    },
  ];

  return { rows, footerRows };
};
